import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { DndDropEvent } from 'ngx-drag-drop';
import { cloneDeep, deburr } from 'lodash';

import { environment } from '@env/environment';
import { I18nService, Logger, untilDestroyed, UrlParamsService } from '@app/core';
import {
  AppService,
  DocumentService,
  DynamicDocumentService,
  ErrorHandlerService,
  GoogleAnalyticsService,
  NotyService,
  TemplateService,
  UserService,
  VerificationService,
  WhitelabelService
} from '@app/services';
import { GooglePickerService } from '@app/services/google-picker.service';
import {
  ActionEnum,
  CustomSignerInput,
  CustomSlimTemplate,
  DateFormatEnum,
  DocumentAdditionalConfigTab,
  DocumentInput,
  DocumentMainTab,
  DocumentSignatureTypeEnum,
  DynamicTemplateSigner,
  DynamicTemplateThemeEnum,
  DynamicTemplateVariable,
  DynamicTemplateVariableInput,
  FloatingField,
  FooterTypeEnum,
  FormSteps,
  GraphQLResponse,
  GTMEventType,
  LocaleInput,
  OrderByEnum,
  PdfaEnum,
  PositionElementEnum,
  SecurityVerificationEnum,
  SecurityVerificationType,
  SignerInput,
  SignerInputContext,
  SignerMainAttributes,
  User,
  UserBiometricValidation,
  UserBiometricValidationStatusEnum,
  Whitelabel
} from '@app/models';
import { AlertModalService, LoaderService, PdfViewerComponent, SelectOption, SetMissingUserInfoModalService } from '@app/shared';
import { ValidEmailDirective, ValidNameDirective } from '@app/shared/validators';
import { DuplicateTemplateModalService } from './duplicate-template-modal/duplicate-template-modal.service';
import { UniqueSignersPipe } from '../shared/unique-signers.pipe';
import { DocumentFormService } from '../shared/document-form.service';
import { DocumentConfigsFormComponent } from '../shared/document-configs-form/document-configs-form.component';
import { AlertDocumentsLimitModalService } from '../shared/alert-documents-limit-modal/alert-documents-limit-modal.service';
import { AlertCreditsLimitModalService } from '../shared/alert-credits-limit-modal/alert-credits-limit-modal.service';
import { idColors } from '../shared/floating-fields-id-colors';
import { DynamicDocumentViewerComponent } from '../shared/dynamic-document-viewer/dynamic-document-viewer.component';
import { VerificationModalService } from '@app/pages/documents/shared/verification-modal/verification-modal.service';

const log = new Logger('DocumentsNewComponent');

@Component({
  selector: 'app-documents-new',
  templateUrl: './documents-new.component.html',
  styleUrls: ['./documents-new.component.scss'],
  standalone: false
})
export class DocumentsNewComponent implements OnInit, AfterViewInit, OnDestroy {
  document = { name: '', expiration: {}, configs: {}, locale: {} } as DocumentInput;
  signers: CustomSignerInput[] = [];
  path: string;
  file: File;
  markdown: string;
  mergeFiles: { file: File; path: string; pdfa: PdfaEnum; uploading?: boolean; error?: boolean }[] = [];
  isMergeFiles = false;
  currentUser: User;
  formStep = FormSteps.signers;
  isLoading = false;
  isUploadingFile = false;
  isSettingFloatingFields = false;
  templates: CustomSlimTemplate[] = [];
  templatesPageNumber: number;
  isTemplatesLastPage = false;
  validationErrors: { [k: string]: any } = {};
  isQualified = false;
  isMobile: boolean;
  isDocumentWhitelabelLoaded = false;
  isLockedOrder = false;
  isLockedSignatureType = false;
  countriesFiltered: SelectOption[] = [];
  searchLocaleInputText = '';
  backLink = '/documentos/todos';
  showAddMeToList = true;
  documentWhitelabel: Whitelabel = {
    domain: null,
    sendgrid_domain: null,
    sendgrid_email_user: null,
    is_darkmode: false,
    main_color: '#1768e1',
    logo: '/assets/Autentique.svg',
    pdf_logo: '/assets/autentique-logo.svg',
    pdf_logo_mini: '/assets/autentique-logo.svg'
  };
  currentDocumentMainTab: DocumentMainTab = DocumentMainTab.FileUpload;
  currentAdditionalConfigTab: DocumentAdditionalConfigTab = DocumentAdditionalConfigTab.AdditionalConfigDocument;
  pageTopTabLoad: { [key in DocumentMainTab | DocumentAdditionalConfigTab]?: boolean } = {
    [DocumentMainTab.FileUpload]: true,
    [DocumentAdditionalConfigTab.AdditionalConfigDocument]: true
  };
  isDynamicTemplate = false;
  signersReady = false;
  creatorIsSigner = false;
  templateId?: string;
  dynamicTemplateContent?: string;
  dynamicTemplateTheme?: DynamicTemplateThemeEnum;
  templateVariables?: DynamicTemplateVariable[];
  renderedDocumentScale = 1;
  isAutentiqueUser = false;

  readonly isElectron = environment.electron;
  readonly FormSteps = FormSteps;
  readonly PositionElementEnum = PositionElementEnum;
  readonly SecurityVerificationEnum = SecurityVerificationEnum;
  readonly idColors = idColors;
  readonly countries: SelectOption[] = [];
  readonly languages: SelectOption[] = [];
  readonly acceptedFileMimeTypes = [
    'application/pdf',
    'text/rtf',
    'text/html',
    'text/plain',
    'application/msword',
    'application/excel',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.presentation',
    'application/vnd.google-apps.spreadsheet'
  ];
  readonly DocumentMainTab = DocumentMainTab;
  readonly DocumentAdditionalConfigTab = DocumentAdditionalConfigTab;
  readonly ActionEnum = ActionEnum;
  readonly SignerInputContext = SignerInputContext;

  @ViewChild('pdfViewer', { static: false }) pdfViewerComponent!: PdfViewerComponent;
  @ViewChild('configsForm', { static: false }) private configsFormComponent!: DocumentConfigsFormComponent;
  @ViewChild('documentUploadWrapper', { static: false }) private documentUploadWrapper: ElementRef<HTMLElement>;
  @ViewChild('renderedTemplate', { static: false }) renderedTemplate!: DynamicDocumentViewerComponent;

  private lastFileName: string;
  private navigationState: { [k: string]: any };

  constructor(
    public documentService: DocumentService,
    public appService: AppService,
    public translateService: TranslateService,
    public urlParamsService: UrlParamsService,
    private userService: UserService,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private loaderService: LoaderService,
    private templateService: TemplateService,
    private dynamicDocumentService: DynamicDocumentService,
    private whitelabelService: WhitelabelService,
    private alertModalService: AlertModalService,
    private googlePickerService: GooglePickerService,
    private router: Router,
    private documentFormService: DocumentFormService,
    private uniqueSigners: UniqueSignersPipe,
    private duplicateTemplateModalService: DuplicateTemplateModalService,
    private setMissingUserInfoModalService: SetMissingUserInfoModalService,
    private alertDocumentsLimitModal: AlertDocumentsLimitModalService,
    private alertCreditsLimitModal: AlertCreditsLimitModalService,
    private i18nService: I18nService,
    private activatedRoute: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private googleAnalyticsService: GoogleAnalyticsService,
    private verificationService: VerificationService
  ) {
    this.navigationState = router.getCurrentNavigation().extras.state || {};
    this.languages = this.i18nService.supportedLanguagesAsSelectOptions || null;
    this.countries = this.i18nService.countriesWithCodes || null;
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe({
      next: () => {
        this.updateBackLink();
      }
    });
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 992;
    this.formStep = this.isMobile ? FormSteps.mobileFile : FormSteps.signers;
    this.countriesFiltered = this.countries;

    this.userService
      .watchCurrentUser()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: user => (this.currentUser = user)
      });

    this.userService.getCurrentUser().subscribe({
      next: user => {
        this.isAutentiqueUser = this.isAutnq(user);

        if (user && !user.currentPermissions.create_documents) {
          this.userService.permissionCheck(user.currentPermissions.create_documents);
        } else if (user && (!user.email || !user.email_verified_at)) {
          this.setMissingUserInfoModalService.openIfMissingUserInfo({ ignoreCpf: true, dismissAsError: true, ignoreLocale: true, ignoreBirthdate: true }).subscribe({
            error: () => this.router.navigate(['/'])
          });
        } else if (user && this.navigationState.document && this.navigationState.signers) {
          this.document = this.navigationState.document;
          this.currentDocumentMainTab = this.navigationState?.currentTab || this.currentDocumentMainTab;
          this.isQualified = this.document?.qualified;
          this.formStep = this.isMobile ? FormSteps.mobileFile : FormSteps.signers;
          this.isLockedOrder = this.navigationState.isLockedOrder;
          this.isLockedSignatureType = this.navigationState.isLockedSignatureType;

          if (this.navigationState.file) {
            if (!this.navigationState?.isDynamicTemplate) {
              this.signers = this.navigationState.signers;
              setTimeout(() => this.selectFile(this.navigationState.file, false));
            } else {
              this.setDynamicTemplateSigners(user);
              this.setDynamicTemplate();
            }
          }
        } else if (user) {
          this.currentDocumentMainTab = this.navigationState?.currentTab || this.currentDocumentMainTab;

          this.isLockedSignatureType = user.member.configs.signature_type !== DocumentSignatureTypeEnum.Unlocked;
          this.isLockedOrder = user.member.configs.ordered_signatures !== null;
          this.document.qualified = user.member.configs.signature_type === DocumentSignatureTypeEnum.Qualified;
          this.document.sortable = user.member.configs.ordered_signatures || false;
          this.document.footer_type = user.additional_configs.footer_type || FooterTypeEnum.Standard;

          this.document.refusable = user.additional_configs.refusable || false;
          this.document.footer = user.additional_configs.footer || null;
          this.document.whatsapp_template = user.member.configs.whatsapp_template || null;
          this.document.email_template_id = user.member.configs.email_template || null;
          this.document.stop_on_rejected = user.additional_configs.stop_on_rejected ?? true;
          this.document.new_signature_style = user.additional_configs.new_signature_style || false;
          this.document.show_audit_page = user.additional_configs.show_audit_page !== false;
          this.document.ignore_birthdate = user.member.configs.ignore_birthdate || false;

          const prepareLocaleInput = this.i18nService.prepareLocaleInput();
          this.document.locale = {
            country: user.member.configs.locale?.country || user.locale?.country || prepareLocaleInput.country,
            language: user.member.configs.locale?.language || user.locale?.language || prepareLocaleInput.language,
            timezone: user.member.configs.locale?.timezone || user.locale?.timezone || prepareLocaleInput.timezone,
            date_format: user.member.configs.locale?.date_format || user.locale?.date_format || prepareLocaleInput.date_format
          } as LocaleInput;

          this.document.ignore_cpf = user.member.configs.ignore_cpf || false;
          this.changeDocumentConfigCountry(this.document.locale.country); // set document configs based on country

          // premium features:
          if (user.subscription.has_premium_features) {
            this.document.reminder = user.additional_configs.reminder || null;
            this.document.expiration.days_before = user.additional_configs.days_before || null;
            this.document.configs.lock_user_data = user.additional_configs.lock_user_data || null;
            this.document.configs.signature_appearance = user.additional_configs.signature_appearance || null;
            this.document.configs.notification_finished = user.member.configs.notification_finished || null;
            this.document.configs.notification_signed = user.member.configs.notification_signed || null;
            this.document.scrolling_required = user.additional_configs.scrolling_required || false;
          }

          if (user.subscription.has_premium_features) {
            const documentCC = (user.member.configs.locked_cc || []).filter(email => !!email);
            this.document.cc = documentCC.map(email => ({ email: email.trim() }));
            this.document.reply_to = user.member.configs.locked_reply_to || '';
          }

          const lockedSigners: CustomSignerInput[] = user.member.configs.locked_signers?.length
            ? this.appService.parseLockSignerToCustomSignerInput(user.member.configs.locked_signers, this.document.qualified)
            : [];
          (lockedSigners || []).forEach(signer => {
            // prevenir erros com signers ja salvos
            const signerClone = cloneDeep(signer);
            if (!user.subscription.has_premium_features) {
              signerClone._extra.showSmsVerification = false;
              signerClone._extra.showCpfVerification = false;
            }
            if (!user.subscription.has_premium_features || (!signerClone._extra?.documentType && !signerClone._extra?.phoneNumber)) {
              // signerClone._extra.documentType = SecurityVerificationEnum.Upload;
              signerClone._extra.phoneNumber = '';
            }
            if (this.document?.locale?.country !== 'BR' && signerClone._extra.documentType === SecurityVerificationEnum.PfFacial) {
              signerClone._extra.showSecurityVerifications = false;
              signerClone._extra.documentType = SecurityVerificationEnum.Manual;
            }
            this.signers.push(signerClone);
          });
        }

        if (this.isWhitelabel()) {
          this.whitelabelService.loadWhitelabelByDomain({ fetchPolicy: 'cache-first' }).subscribe({
            next: whitelabel => {
              this.documentWhitelabel = whitelabel;
              this.documentWhitelabel.logo = whitelabel.logo || this.whitelabelService.logo;
              this.documentWhitelabel.pdf_logo = whitelabel.pdf_logo || this.whitelabelService.pdfLogo;
              this.documentWhitelabel.pdf_logo_mini = whitelabel.pdf_logo_mini || this.whitelabelService.pdfLogoMini;
              this.isDocumentWhitelabelLoaded = true;
            }
          });
        } else {
          this.documentWhitelabel.logo = this.whitelabelService.logo;
          this.documentWhitelabel.pdf_logo = this.whitelabelService.pdfLogo;
          this.documentWhitelabel.pdf_logo_mini = this.whitelabelService.pdfLogoMini;
          this.isDocumentWhitelabelLoaded = true;
        }

        setTimeout(() => this.onSignerChange(this.signers[this.signers.length - 1]));
      }
    });

    this.loadTemplates();
    this.activatedRoute.fragment.subscribe({
      next: (fragment: string | null) => {
        if (Object.values(DocumentMainTab).includes(fragment as DocumentMainTab)) {
          const tab = fragment as DocumentMainTab;
          this.changeMainTab(tab);
        }
      }
    });
  }

  ngAfterViewInit() {
    this.documentUploadWrapper.nativeElement.addEventListener('dragover', event => event.preventDefault(), false);
    this.documentUploadWrapper.nativeElement.addEventListener(
      'drop',
      event => {
        event.stopPropagation();
        event.preventDefault();
        const files: FileList = event.dataTransfer.files;

        if (files) {
          if (files[0]) {
            this.removeFile();
            this.addMultipleFiles(files, false);
          } else {
            this.notyService.error(this.translateService.instant('notyService.fileNotAdded'));
          }
        } else {
          this.notyService.error(this.translateService.instant('notyService.invalidFileType'));
        }
      },
      false
    );
  }

  ngOnDestroy() {
    this.resetWhitelabelInfo();
  }

  setFormStep(step: FormSteps) {
    if (!this.isDynamicTemplate) {
      this.signers = this.uniqueSigners.transform(this.signers || [], this.document.qualified);
    }
    this.pdfViewerComponent.removeFloatingFieldsWhenQualifiedDocument();
    if (step === FormSteps.signaturePositions) {
      this.signers.filter(signer => !!signer?._extra?.isQualified).forEach(signer => this.pdfViewerComponent.sanitizeFloatingFieldsForQualifiedSigner(this.uniqueSigners.identification(signer)));
    }
    if (!this.isDynamicTemplate) {
      this.addNewSigner();
    }

    this.currentAdditionalConfigTab = DocumentAdditionalConfigTab.AdditionalConfigDocument; // se tiver alguma iteração ele reseta a currentAdditionalConfigTab para document
    if (step === FormSteps.mobileFile || (step === FormSteps.signaturePositions && this.formStep !== FormSteps.extraConfigurations)) {
      if ((!this.isMobile || this.formStep === FormSteps.signers) && this.uniqueSigners.transform(this.signers, this.document.qualified).length === 0) {
        this.notyService.error(this.translateService.instant('notyService.noSignatoriesAdded'));
      } else if (!this.isMobile && this.isDynamicTemplate && this.verifyDuplicateSigners()) {
        this.notyService.error(this.translateService.instant('notyService.duplicateSignersWithSameAction'));
      } else if (step === FormSteps.signaturePositions && !(this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow ? this.markdown : this.file)) {
        if (this.isMergeFiles) {
          this.submitMergeFiles();
          this.formStep = step;
        } else {
          const errorKey = this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow ? 'notyService.documentWhatsappCannotBeEmpty' : 'notyService.noFileSelected';
          this.notyService.error(this.translateService.instant(errorKey));
        }
      } else {
        if (this.currentDocumentMainTab !== DocumentMainTab.WhatsappFlow) {
          if (!this.isMobile && this.isDynamicTemplate) {
            this.createFileFromJSON();
            this.formStep = FormSteps.extraConfigurations;
          }
          // Assinaturas visíveis serão adicionadas posteriormente como campos na criação do modelo
          else {
            this.formStep = step;
          }
        }
        if (this.markdown && this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow) {
          this.setDocumentNameFromMarkdown();
          this.formStep = FormSteps.extraConfigurations;
        }
        setTimeout(() => window.scrollTo(0, 0));
      }
    } else if (step === FormSteps.extraConfigurations) {
      if (this.isMobile && this.formStep === FormSteps.signers && this.isDynamicTemplate && this.verifyDuplicateSigners()) {
        this.notyService.error(this.translateService.instant('notyService.duplicateSignersWithSameAction'));
      } else {
        this.formStep = step;

        if (this.isMobile && this.isDynamicTemplate) {
          this.createFileFromJSON();
        }
        // Se o navigationState tiver folderId e folderName sera o FormComponent com o folder selecionado
        if (this.navigationState?.folder) {
          this.configsFormComponent.folder = this.navigationState.folder;
        }
      }
    } else {
      if (this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow) {
        this.formStep = FormSteps.signers;
      } else {
        this.formStep = step;
      }
    }
  }

  createFileFromJSON() {
    const json = JSON.stringify({
      signers: this.getSignersParamForCreateDocument(),
      variables: this.templateVariables,
      content: this.navigationState.templateHTML,
      theme: this.dynamicTemplateTheme
    });
    const file: File = new File([new Blob([json], { type: 'text/plain' })], this.file.name.replace('.html', '.json'), { type: 'text/json' });
    this.file = file;

    setTimeout(() => this.uploadFile(file).subscribe());
    this.setDocumentNameFromFile(file);
  }

  submitDocument() {
    if (this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow) {
      this.path = undefined;
      this.file = this.createMarkdownFile();
    }

    if (!this.file && this.isMergeFiles) {
      this.submitMergeFiles().subscribe({
        next: () => {
          this.isMergeFiles = false;
          this.submitDocument();
        }
      });
    } else if (!(this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow ? this.markdown : this.file)) {
      const errorKey = this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow ? 'notyService.documentWhatsappCannotBeEmpty' : 'notyService.noFileSelected';
      this.notyService.error(this.translateService.instant(errorKey));
    } else if (this.uniqueSigners.transform(this.signers, this.document.qualified).length === 0) {
      this.notyService.error(this.translateService.instant('notyService.noSignatoriesAdded'));
    } else if (!this.validateDocument(this.document, this.signers)) {
      this.formStep = FormSteps.signers;
    } else if (!this.path) {
      this.setMissingUserInfoModalService
        .openIfMissingUserInfo({ ignoreCpf: true, ignoreBirthdate: true })
        .pipe(
          tap(() => this.startUploadFlags()),
          switchMap(() => this.uploadFile(this.file)),
          tap({
            next: () => {},
            error: error => this.errorHandlerService.handle(error)
          }),
          tap(() => (this.isLoading = true)),
          switchMap(() => this.createDocument()),
          finalize(() => this.finalizeUploadFlags())
        )
        .subscribe({
          next: data => {
            this.router.navigate(['/documentos/', data.id], { state: { fromCreateDocument: true } });
            this.sendDocumentIdWithPostMessage(data.id);
            this.googleAnalyticsService.emitGTMEvent(GTMEventType.DocumentCreated, this.currentUser.id);
          }
        });
    } else {
      this.setMissingUserInfoModalService
        .openIfMissingUserInfo({ ignoreCpf: true, ignoreBirthdate: true })
        .pipe(
          tap(() => (this.isLoading = true)),
          switchMap(() => this.createDocument()),
          finalize(() => (this.isLoading = false))
        )
        .subscribe({
          next: data => {
            this.router.navigate(['/documentos/', data.id], { state: { fromCreateDocument: true } });
            this.sendDocumentIdWithPostMessage(data.id);
            this.googleAnalyticsService.emitGTMEvent(GTMEventType.DocumentCreated, this.currentUser.id);
          }
        });
    }
  }

  sendDocumentIdWithPostMessage(documentId: string) {
    if (!documentId.length) return;
    window.parent.postMessage(documentId, '*');
  }

  createDocument() {
    const document = this.configsFormComponent.parsedDocumentInput();

    if (this.isDynamicTemplate) {
      document.sortable = false;
      document.qualified = false;
    }

    const folderId = this.configsFormComponent.folder?.id;
    const params = this.getParamsForCreateDocument(document, folderId);

    return this.documentService.create(params).pipe(
      tap(() => {
        const additionalConfigs = {
          additional_configs: this.configsFormComponent.parseAdditionalConfigs(document),
          member_configs: {
            ...this.configsFormComponent.parseMemberConfigs(document),
            locked_signers: this.appService.parseLockSigners(this.signers, document.qualified),
            folder_id: folderId || null,
            ordered_signatures: this.isLockedOrder ? document.sortable : null,
            signature_type: (!this.isLockedSignatureType && DocumentSignatureTypeEnum.Unlocked) || (document.qualified && DocumentSignatureTypeEnum.Qualified) || DocumentSignatureTypeEnum.Advanced
          }
        };
        if (this.isDynamicTemplate && additionalConfigs.member_configs.locked_signers) {
          delete additionalConfigs.member_configs.locked_signers;
        }
        return this.userService.updateUserAdditionalConfigs(additionalConfigs).subscribe();
      }),
      tap(() => this.userService.getCurrentUser({ fetchPolicy: 'network-only' }).subscribe()),
      catchError(error => {
        if (!this.isDynamicTemplate) {
          this.addNewSigner();
          this.onSignerChange(this.signers[this.signers.length - 1]); // Moves empty signer to end so it doesn't mess de validationErrors' positions
        }

        const handledError = this.errorHandlerService.handle(error, {
          ignoreGraphqlErrorNotifications: true,
          validationMode: true,
          ignoreNotificationOn: ['unavailable_verifications_credits', 'user_biometric_verification_required']
        });
        this.validationErrors = handledError.validation || {};
        if (Object.keys(this.validationErrors).length > 0) {
          if (this.validationErrors.signers) {
            this.validationErrors.signers.forEach((currElement: any, index: any) => {
              if (currElement.configs?.cpf || currElement.configs?.cnpj) {
                this.signers[index]._extra.showOptions = true;
                this.signers[index]._extra.showCpfVerification = true;
              }
            });
            this.formStep = FormSteps.signers;
          } else {
            this.formStep = FormSteps.extraConfigurations;
          }

          this.notyService.error(this.translateService.instant('error_validation'));
        } else if (handledError.message === 'could_not_upload_file') {
          this.removeFile();
        } else if (handledError.message === 'unavailable_verifications_credits') {
          this.alertCreditsLimitModal.open({ currentUser: this.currentUser }).subscribe();
        } else if (handledError.message === 'user_biometric_verification_required') {
          this.verificationService.validateUserCreateDocument().subscribe({
            next: (data: UserBiometricValidation) => {
              if (data?.status === UserBiometricValidationStatusEnum.Completed) {
                this.submitDocument();
              }
            }
          });
        } else if (!['without_archiving_permission', 'without_permission'].includes(handledError.message)) {
          this.formStep = FormSteps.signers;
          this.notyService.error(this.translateService.instant('error_unknown'));
        }

        if (this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow) {
          this.path = undefined;
          this.file = undefined;
        }
        return throwError(() => error);
      }),
      untilDestroyed(this)
    );
  }

  getParamsForCreateDocument(currentDocument?: DocumentInput, currentFolderId?: string) {
    const document = currentDocument || this.configsFormComponent.parsedDocumentInput();
    const folderId = currentFolderId || this.configsFormComponent.folder?.id;

    const signers = this.getSignersParamForCreateDocument();

    if (this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow) {
      // se for whatsapp, o documento é markdown e não pode ser qualificado
      this.document.qualified = false;
    }

    let params = { document, signers, path: this.path, folderId };

    return params;
  }

  getSignersParamForCreateDocument(): Array<SignerInput> {
    const signers = this.documentFormService.transformToSignerInputs(
      this.document,
      this.signers.filter((signer: CustomSignerInput | null) => signer !== null),
      this.pdfViewerComponent.getFloatingFields(),
      this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow
    );

    signers.map((signer: any) => {
      delete signer.__typename;
      delete signer.configs?.__typename;
      for (let i = 0; i < signer.security_verifications?.length; i++) {
        if (signer.security_verifications[i].__typename) delete signer.security_verifications[i].__typename;
      }

      if (!this.currentUser.subscription.has_premium_features) {
        delete signer.configs;
      }

      for (const key in signer) {
        if (signer[key] === null) delete signer[key];
      }
    });

    return signers;
  }

  uploadFile(file: File) {
    this.loaderService.disableHttpLoader = true;

    return this.documentService.uploadFile({ file }).pipe(
      tap(data => (this.path = data.path)),
      finalize(() => (this.loaderService.disableHttpLoader = false)),
      untilDestroyed(this)
    );
  }

  verifyDuplicateSigners(): boolean {
    let verifiedSigners: CustomSignerInput[] = new Array();

    this.signers.forEach(signer => {
      if (
        verifiedSigners?.findIndex(
          verifiedSigner => verifiedSigner?.action == signer?.action && verifiedSigner[verifiedSigner?._extra?.signerMainAttribute] == signer[signer?._extra?.signerMainAttribute]
        ) === -1
      ) {
        verifiedSigners.push(signer);
      }
    });

    return this.signers.length !== verifiedSigners?.length;
  }

  selectFile(file: File, isFileMerge: boolean) {
    this.file = file;
    this.isDynamicTemplate = false;
    if (this.file) {
      if (!this.acceptedFileMimeTypes.includes(this.file.type)) {
        this.notyService.error(this.translateService.instant('notyService.invalidFileTypeSent'), 15000);
        this.removeFile();
      } else if (!this.validateFileSizeLimit(this.file.size)) {
        this.removeFile();
      } else {
        if (!isFileMerge) {
          this.setDocumentNameFromFile(this.file);
        }
        this.startUploadFlags();
        this.uploadFile(this.file)
          .pipe(finalize(() => this.finalizeUploadFlags()))
          .subscribe({
            next: data => {
              const mergeFile = {
                file,
                path: data.path,
                pdfa: data.pdfa
              };
              this.mergeFiles.push(mergeFile);
              if (isFileMerge) {
                this.isMergeFile();
              } else {
                this.setPdfViewer({ url: this.appService.storagePath(data.path) }).subscribe();
              }
            },
            error: error => {
              if (error?.graphQLErrors && error?.graphQLErrors[0]?.message === 'unavailable_credits') {
                this.alertDocumentsLimitModal.open({ currentUser: this.currentUser }).subscribe();
              } else {
                this.errorHandlerService.handle(error);
              }
              this.removeFile();
            }
          });
      }
    }
  }

  addMultipleFiles(files: FileList, isFileMerge: boolean) {
    if (files) {
      this.file = files[0];
      if (!isFileMerge) {
        this.setDocumentNameFromFile(this.file);
      }

      if (files.length === 1) {
        this.selectFile(files[0], isFileMerge);
      } else {
        const totalSize = Array.from(files).reduce((total, file) => total + file.size, 0);

        if (!this.validateFileSizeLimit(totalSize)) {
          this.removeFile();
          return;
        }

        this.startUploadFlags();

        Array.from(files).forEach(file => {
          this.mergeFiles.push({ file, path: '', pdfa: null, uploading: true });
        });
        this.isMergeFile();

        this.uploadFilesSequentially().subscribe({
          error: error => {
            if (error?.graphQLErrors && error?.graphQLErrors[0]?.message === 'unavailable_credits') {
              this.alertDocumentsLimitModal.open({ currentUser: this.currentUser }).subscribe();
            } else {
              this.errorHandlerService.handle(error);
            }
            this.removeFile();
          }
        });
      }
    }
  }

  setDynamicTemplate() {
    this.file = this.navigationState.file;
    this.document.sortable = false;
    this.document.qualified = false;
    this.isDynamicTemplate = this.navigationState.isDynamicTemplate;

    this.templateId = this.navigationState?.templateId;
    this.dynamicTemplateTheme = this.navigationState?.templateTheme;

    if (this.navigationState?.variables) {
      this.templateVariables = this.navigationState.variables.filter((variable: DynamicTemplateVariableInput) => !!variable.data_signer_id);
    }

    if (this.navigationState?.templateHTML) {
      this.dynamicTemplateContent = this.appService.cleanAttqDocumentMargins(this.navigationState?.templateHTML);
    }

    if (this.navigationState?.templateLang) {
      const lang = this.languages.find(lang => lang.value.replace(/-|_/g, '').toLowerCase() == this.navigationState.templateLang.replace(/-|_/g, '').toLowerCase()).key;
      this.changeLocaleInput('language', lang);
    }
  }

  setDynamicTemplateSigners(creator: User) {
    this.signers = this.dynamicDocumentService.retrieveSignersData(this.navigationState.signers as DynamicTemplateSigner[]).map(signer => {
      signer._extra.isEditable = !signer._extra.isEditable;
      return signer;
    });

    if (this.signers[0].variable_id === 0) this.creatorIsSigner = true;
    if (this.creatorIsSigner) {
      this.signers[0].email = creator.email;
    }

    setTimeout(() => (this.signersReady = this.verifySignersAreReady()));
  }

  updateRenderedDocumentScale() {
    this.renderedDocumentScale = this.appService.scaleRenderedDocumentToFit(this.renderedTemplate?.editorRef.nativeElement, this.isMobile, { widthFix: 384, margin: 20 });
    this.changeDetectorRef.detectChanges();
  }

  loadTemplates(options: { page?: number } = {}) {
    this.isLoading = true;
    this.templatesPageNumber = options.page || 1;
    this.dynamicDocumentService
      .allTemplates({ limit: 10, page: this.templatesPageNumber, orderBy: { field: 'created_at', direction: OrderByEnum.Desc } })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (page: any) => {
          this.templates = (this.templatesPageNumber === 1 ? [] : this.templates || []).concat(
            page.data.map((template: CustomSlimTemplate) => ({
              ...template,
              is_dynamic_template: true
            }))
          );
          this.isTemplatesLastPage = page.current_page >= page.last_page;
        },
        error: (error: GraphQLResponse) => this.errorHandlerService.handle(error)
      });
    this.templateService
      .allTemplates({ limit: 10, page: this.templatesPageNumber, orderBy: { field: 'created_at', direction: OrderByEnum.Desc } })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: page => {
          this.templates = this.templates?.concat(
            page.data.map((template: CustomSlimTemplate) => ({
              ...template,
              is_dynamic_template: false
            }))
          );
          this.isTemplatesLastPage = this.isTemplatesLastPage && page.current_page >= page.last_page;

          this.templates?.sort((a: CustomSlimTemplate, b: CustomSlimTemplate) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  editTemplate(template: CustomSlimTemplate) {
    if (template.is_dynamic_template) {
      this.router.navigate(['/dinamicos', template.id, 'editar']);
    } else {
      this.router.navigate(['/modelos', template.id, 'editar']);
    }
  }

  openDuplicateTemplateModal(template: CustomSlimTemplate) {
    this.duplicateTemplateModalService.open({ template }).subscribe({
      next: (newTemplate: CustomSlimTemplate) => this.templates.unshift({ ...newTemplate, is_dynamic_template: template.is_dynamic_template })
    });
  }

  deleteTemplate(template: CustomSlimTemplate) {
    this.alertModalService
      .warning({
        text: this.translateService.instant('alerts.deleteTemplate'),
        confirmButtonText: this.translateService.instant('button.exclude')
      })
      .pipe(
        switchMap(() => {
          this.isLoading = true;
          if (template.is_dynamic_template) {
            return this.dynamicDocumentService.delete({ id: template.id });
          }
          return this.templateService.delete({ id: template.id });
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: () =>
          this.templates.splice(
            this.templates.findIndex(item => {
              if (template.is_dynamic_template) {
                return item.id === template.id;
              }
              return item.id === template.id;
            }),
            1
          ),
        error: error => this.errorHandlerService.handle(error)
      });
  }

  openGooglePicker(isFileMerge: boolean) {
    this.googlePickerService.open().subscribe({
      next: googleFile => {
        const fileName = googleFile.data.type !== 'application/pdf' ? googleFile.name || '' : (googleFile.name || '').replace(/\.[A-z0-9]+$/, '') + '.pdf';
        const file = new File([googleFile.data], fileName, { type: googleFile.data.type, lastModified: googleFile.lastModified });
        this.selectFile(file, isFileMerge);
      }
    });
  }

  addNewSigner(email?: string, invertOrder?: boolean) {
    const mainAttribute = this.currentDocumentMainTab === DocumentMainTab.WhatsappFlow && !email ? SignerMainAttributes.Whatsapp : SignerMainAttributes.Email;
    this.signers[invertOrder ? 'unshift' : 'push']({
      [mainAttribute]: email || '',
      action: this.documentService.roles[0],
      configs: {},
      _extra: { signerMainAttribute: mainAttribute, phoneNumber: '', documentType: SecurityVerificationEnum.Manual, fallbackEnabled: true, isEditable: true },
      security_verifications: []
    });

    this.signersReady = this.verifySignersAreReady();
  }

  removeSigner(signerIndex: number) {
    this.signers[signerIndex] = null;
    this.signersReady = this.verifySignersAreReady();
  }

  verifySignersAreReady() {
    if (!this.isDynamicTemplate) return true;

    let signersWithFields: string[] = [];
    if (this.templateVariables)
      this.templateVariables
        .filter(variable => !!variable.data_signer_id && variable.data_signer_id !== 0)
        .forEach((element: DynamicTemplateVariable) => {
          let assignedSigner = element.data_signer_id.toString();
          if (!signersWithFields.includes(assignedSigner) && this.signers[parseInt(assignedSigner)] !== null) signersWithFields.push(assignedSigner);
        });

    let notNullSigners: CustomSignerInput[] = this.signers.filter(signer => signer !== null);
    return (
      notNullSigners.filter(thisSigner => !!thisSigner && !!thisSigner[thisSigner._extra.signerMainAttribute]).length == notNullSigners.length && notNullSigners.length >= signersWithFields.length
    );
  }

  setFloatingFieldFor(signer: CustomSignerInput, element: PositionElementEnum, idColor: string) {
    this.isSettingFloatingFields = true;
    this.pdfViewerComponent
      .floatingFieldsSettingModeFor({
        element,
        idColor,
        identification: this.uniqueSigners.identification(signer),
        signerQualified: !!signer?._extra?.isQualified
      })
      .pipe(finalize(() => (this.isSettingFloatingFields = false)))
      .subscribe();
  }

  cancelSettingFloatingFields() {
    this.pdfViewerComponent.disableFloatingFieldsSettingMode();
    this.isSettingFloatingFields = false;
  }

  onSignerChange(signer: CustomSignerInput) {
    this.validationErrors = {};

    const signerFieldsLength = this.signers.length;
    const usedSignerFieldsLength = this.isDynamicTemplate ? this.signers.length - 1 : this.signers.filter(thisSigner => !!thisSigner[thisSigner._extra.signerMainAttribute]).length;
    const currentSignerIndex = this.signers.findIndex(thisSigner => thisSigner === signer);

    if (signerFieldsLength === usedSignerFieldsLength) {
      this.addNewSigner();
    } else if (signerFieldsLength > usedSignerFieldsLength + 1) {
      this.signers
        .map((thisSigner, index) => (!thisSigner[thisSigner._extra.signerMainAttribute] ? index : -1))
        .filter(index => index >= 0 && index !== currentSignerIndex)
        .forEach(index => this.signers.splice(index, 1));
    }

    this.signersReady = this.verifySignersAreReady();
  }

  removeFile(step?: FormSteps) {
    this.file = undefined;
    this.path = undefined;
    this.mergeFiles = [];
    this.isMergeFiles = false;
    this.formStep = step || FormSteps.signers;
    this.signers = this.signers.filter((signer: CustomSignerInput | null) => signer !== null);
    this.signers.forEach(signer => delete signer.positions);
    this.pdfViewerComponent.reset();
    this.pdfViewerComponent.clearFloatingFields();

    if (this.isDynamicTemplate) {
      this.signers = [];
      this.addNewSigner();
    }
    this.isDynamicTemplate = false;

    setTimeout(() => window.scrollTo(0, 0));
  }

  isMergeFile() {
    this.file = undefined;
    this.path = undefined;
    this.signers.forEach(signer => delete signer.positions);
    this.pdfViewerComponent.reset();
    this.pdfViewerComponent.clearFloatingFields();
    this.isMergeFiles = true;
  }

  isMergeFileMobile(step?: FormSteps) {
    this.formStep = step || FormSteps.signers;
    this.isMergeFile();
  }

  deleteMergeFile(index: number) {
    this.mergeFiles.splice(index, 1);
    if (this.mergeFiles.length === 0) {
      this.isMergeFiles = false;
      this.removeFile();
    }
  }

  submitMergeFiles() {
    const totalSize = this.mergeFiles.reduce((total, file) => total + file.file.size, 0);

    if (!this.validateFileSizeLimit(totalSize)) {
      return of(null);
    }

    this.startUploadFlags();
    this.file = this.mergeFiles[0].file;
    const mergeFilesPath = this.mergeFiles.map(file => file.path);
    const waitUrl$ = this.appService.waitUntilUrlsExist(mergeFilesPath.map(path => this.appService.storagePath(path))).pipe(
      switchMap(() => (mergeFilesPath.length > 1 ? this.documentService.mergeFiles({ files: mergeFilesPath }) : of(this.mergeFiles[0]))),
      tap(data => (this.path = data.path)),
      switchMap(data => this.setPdfViewer({ url: this.appService.storagePath(data.path) })),
      finalize(() => this.finalizeUploadFlags())
    );

    waitUrl$.subscribe({
      next: () => (this.formStep = this.formStep === FormSteps.mobileFile ? FormSteps.signers : this.formStep),
      error: error => this.errorHandlerService.handle(error)
    });

    return waitUrl$;
  }

  onDragAndDropSigner(event: DndDropEvent) {
    this.signers.splice(event.index, 0, this.signers.splice(event.data, 1)[0]);
  }

  onDragAndDropMergeFile(event: DndDropEvent) {
    this.mergeFiles.splice(event.index, 0, this.mergeFiles.splice(event.data, 1)[0]);
  }

  disableFloatingFieldsSettingMode() {
    if (this.isSettingFloatingFields) {
      this.isSettingFloatingFields = false;
      this.pdfViewerComponent.disableFloatingFieldsSettingMode();
    }
  }

  selectFloatingFieldInput(field: FloatingField) {
    this.signers.forEach(signer => (signer._extra.isSelected = field && this.uniqueSigners.identification(signer) === field.identification));
  }

  fillTemplate(template: CustomSlimTemplate) {
    if (template?.is_dynamic_template) {
      this.router.navigate(['/dinamicos', template.id], {
        state: { document: this.document, folder: this.navigationState?.folder, currentTab: this.currentDocumentMainTab }
      });
    } else {
      this.router.navigate(['/modelos', template.id], {
        state: {
          document: this.document,
          signers: this.signers,
          folder: this.navigationState?.folder,
          currentTab: this.currentDocumentMainTab,
          isLockedOrder: this.isLockedOrder,
          isLockedSignatureType: this.isLockedSignatureType
        }
      });
    }
  }

  setQualifiedSignature(value: boolean) {
    this.isQualified = value;
  }

  isMergeFilesTooLarge() {
    if (this.currentUser) {
      const maxKB = this.currentUser.subscription.has_premium_features ? 20000 : 5000;
      return (this.mergeFiles || []).reduce((total, file) => total + file.file.size, 0) / 1024 > maxKB;
    }
  }

  hasMergeFilesWithErrors() {
    return (this.mergeFiles || []).some(file => file.error);
  }

  isUploadingMergeFiles() {
    return (this.mergeFiles || []).some(file => file.uploading);
  }

  private validateFileSizeLimit(sizeInBytes: number): boolean {
    const userHasPaidPlan = !this.userService.isSubscriptionFree(this.currentUser);
    const maxBytes = (userHasPaidPlan ? 20 : 5) * 1024 * 1024;

    if (sizeInBytes > maxBytes) {
      this.notyService.error(this.translateService.instant('notyService.' + (userHasPaidPlan ? 'fileSizeExceedCorporatePlan' : 'fileSizeExceedFreePlan')));
      return false;
    }
    return true;
  }

  changeLocaleInput(type: 'language' | 'country', value: string): void {
    if (type === 'language') {
      this.document.locale.language = value;
    } else if (type === 'country') {
      this.document.locale.country = value;
      this.changeDocumentConfigCountry(this.document.locale.country);
    }
  }

  changeDocumentConfigCountry(value: string): void {
    if (value !== 'BR') {
      this.document.ignore_cpf = true;

      this.signers.forEach(signer => {
        signer._extra.showSecurityVerifications = false;
        signer._extra.documentType = SecurityVerificationEnum.Manual;
      });
    }

    this.document.locale.date_format = DateFormatEnum[value === 'US' ? 'MmDdYyyy' : 'DdMmYyyy'];
  }

  filterDropdown(e: any) {
    window.scrollTo(window.scrollX, window.scrollY + 1);
    const searchString = e.toLowerCase();
    if (!searchString) {
      this.countriesFiltered = this.countries.slice();
      return;
    } else {
      this.countriesFiltered = this.countries.filter(country => deburr(country.value.toLowerCase()).indexOf(deburr(searchString.toLowerCase())) > -1);
    }
    window.scrollTo(window.scrollX, window.scrollY - 1);
  }

  resetWhitelabelInfo() {
    this.documentWhitelabel = {
      domain: null,
      sendgrid_domain: null,
      sendgrid_email_user: null,
      is_darkmode: false,
      main_color: '#1768e1',
      logo: '/assets/Autentique.svg',
      pdf_logo: '/assets/autentique-logo.svg',
      pdf_logo_mini: '/assets/autentique-logo.svg'
    };
  }

  isWhitelabel() {
    return !this.appService.isAutentiqueDomain() && !!this.whitelabelService?.whitelabel?.domain;
  }

  updateBackLink(): void {
    const url = this.appService.previousUrl;
    if ((url || '').includes('/pastas/')) {
      this.backLink = url;
    } else {
      this.backLink = '/documentos/todos';
    }
  }

  changeMainTab(tab: DocumentMainTab): void {
    if (this.currentDocumentMainTab === tab) return;

    // prevent clicks on disabled tab
    if (this.file || this.markdown || this.isMergeFiles) return;

    this.currentDocumentMainTab = tab;
    if (!this.pageTopTabLoad[tab]) {
      this.pageTopTabLoad[tab] = true;
    }

    if (tab === DocumentMainTab.WhatsappFlow) {
      this.document.qualified = false;
      this.signers.map(signer => {
        if (!(signer.email || signer.name || signer.sms || signer.whatsapp)) signer._extra.signerMainAttribute = SignerMainAttributes.Whatsapp;
      });
    } else {
      this.signers.map(signer => {
        if (!(signer.email || signer.name || signer.sms || signer.whatsapp)) signer._extra.signerMainAttribute = SignerMainAttributes.Email;
      });
    }

    this.formStep = FormSteps.signers;
  }

  changeDocumentConfigTab(tab: DocumentAdditionalConfigTab): void {
    if (this.currentAdditionalConfigTab === tab) return;

    this.currentAdditionalConfigTab = tab;
    if (!this.pageTopTabLoad[tab]) {
      this.pageTopTabLoad[tab] = true;
    }
  }

  createMarkdownFile() {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const fileName = `document-${timestamp}.md`;
    const markdownBlob = new Blob([this.markdown], { type: 'text/markdown' });
    return new File([markdownBlob], fileName, { type: 'text/markdown' });
  }

  isPdfa() {
    const result = this.mergeFiles.length === 1 && !!this.mergeFiles[0].pdfa;
    if (result && !this.document.new_signature_style) {
      setTimeout(() => (this.document.new_signature_style = true));
    }
    return result;
  }

  isPdfaInMergeFiles() {
    return this.mergeFiles.some(file => file.pdfa);
  }

  isAutnq(user?: User) {
    user = user || this.currentUser;
    return !!(user && (user.email || '').match(/@autentique.com.br$/)) && user.organization.id !== 10126135;
  }

  private setPdfViewer(options: { url?: string; file?: File }) {
    return this.pdfViewerComponent.setPdf(options).pipe(
      tap({
        error: error => {
          this.notyService.error(this.translateService.instant('notyService.fileConversionError'), false);
          log.error('Error converting file', error);
          this.removeFile();
        }
      })
    );
  }

  private setDocumentNameFromFile(file: File) {
    const fileName = (file.name || '').replace(/\.[A-z0-9]+$/, '');
    if (fileName && (!this.document.name || this.document.name === this.lastFileName)) {
      this.lastFileName = fileName;
      this.document.name = this.lastFileName;
    }
  }

  private setDocumentNameFromMarkdown() {
    if (this.document.name.length !== 0) return;

    const lines = this.markdown.split('\n');
    let title = '';

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith('# ')) {
        title = trimmedLine.replace(/[#*~]/g, '').trim();
        break;
      }
    }
    this.document.name = title;
  }

  private startUploadFlags() {
    this.isLoading = true;
    this.isUploadingFile = true;
  }

  private finalizeUploadFlags() {
    this.isLoading = false;
    this.isUploadingFile = false;
  }

  private uploadFilesSequentially(): Observable<void> {
    const mergeFileIndex = this.mergeFiles.findIndex(mf => mf.uploading);

    if (mergeFileIndex < 0) {
      this.finalizeUploadFlags();
      return of(undefined);
    }

    const mergeFile = this.mergeFiles[mergeFileIndex];

    return this.uploadFile(mergeFile.file).pipe(
      tap(data => {
        const currentIndex = this.mergeFiles.findIndex(mf => mf.file === mergeFile.file);
        if (currentIndex >= 0) {
          this.mergeFiles[currentIndex].path = data.path;
          this.mergeFiles[currentIndex].pdfa = data.pdfa;
          this.mergeFiles[currentIndex].uploading = false;
        }
      }),
      delay(1000),
      switchMap(() => this.uploadFilesSequentially()),
      catchError(error => {
        const currentIndex = this.mergeFiles.findIndex(mf => mf.file === mergeFile.file);
        if (currentIndex >= 0) {
          this.mergeFiles[currentIndex].uploading = false;
          this.mergeFiles[currentIndex].error = true;
        }
        if (error?.graphQLErrors?.[0]?.message === 'unavailable_credits') {
          return throwError(() => error);
        }
        return this.uploadFilesSequentially();
      })
    );
  }

  private validateDocument(document: DocumentInput, signers: CustomSignerInput[]): boolean {
    this.validationErrors = {};
    signers.forEach((signer, index) => {
      if (signer && signer._extra.signerMainAttribute === SignerMainAttributes.Name && signer.name && !ValidNameDirective.isNameValid(signer.name)) {
        this.validationErrors.signers = this.validationErrors.signers || [];
        this.validationErrors.signers[index] = 'error_invalid_name';
      } else if (signer && signer._extra.signerMainAttribute === SignerMainAttributes.Email && signer.email && !ValidEmailDirective.isEmailValid(signer.email)) {
        this.validationErrors.signers = this.validationErrors.signers || [];
        this.validationErrors.signers[index] = 'error_invalid_email';
      }
    });

    return Object.keys(this.validationErrors).length === 0;
  }

  @HostListener('window:keydown', ['$event']) onKeyDown(event: KeyboardEvent) {
    if ((event.key || '').toLowerCase() === 'escape') {
      this.disableFloatingFieldsSettingMode();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.isMobile = window.innerWidth <= 992;

    if (!!this.document && !this.isMobile && this.formStep === FormSteps.mobileFile) {
      this.formStep = FormSteps.signers;
    }

    if (this.isDynamicTemplate) {
      this.updateRenderedDocumentScale();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: Event) {
    event.preventDefault();
    event.returnValue = true;
  }
}
